import React, { Dispatch, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from '../../../../../../ui/toast';
import { Button } from '../../../../../../ui';
import { UploadFileComponent } from '../../../../../../ui/input/FileInput';
import { uploadFileJobDescription } from '../../../../../../utils/api_service/JobOpenings';

const fileTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export type ImportJobDescriptionModalProps = {
  closeModal: () => void;
  setLoading: Dispatch<React.SetStateAction<boolean>>;
};

export const ImportJobDescription: React.FC<ImportJobDescriptionModalProps> = ({
  closeModal,
  setLoading,
}) => {
  const { id } = useParams<{ id: string }>();
  const [uploadedFile, setUploadedFile] = useState<File[] | null>(null);
  const [transcript, setTranscript] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const fileUploaded = uploadedFile ? uploadedFile.length > 0 : false;

  const uploadJobDescription = async () => {
    if (!uploadedFile?.length && !transcript) {
      toast.error('Please select a file or enter a transcript', {
        position: 'bottom-center',
      });
      return;
    }

    setIsImporting(true);

    try {
      const source =
        uploadedFile && uploadedFile?.length > 0 ? uploadedFile[0] : undefined;
      const response = await uploadFileJobDescription(id, source, transcript);
      if (response.ok) {
        toast.success(
          'Job description successfully uploaded and being processed...',
          {
            position: 'bottom-center',
          },
        );
        setLoading(true);
        closeModal();
      } else {
        setIsImporting(false);
        toast.error('Upload unsuccessful. Please try again.', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      setIsImporting(false);
      toast.error('Error uploading job description. Please try again.', {
        position: 'bottom-center',
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 768, mx: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Step 1: Upload File */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.875rem',
                fontWeight: 600,
                bgcolor: 'primary.light',
                color: 'primary.dark',
              }}
            >
              1
            </Avatar>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Upload File
            </Typography>
          </Stack>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <UploadFileComponent
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              validTypes={fileTypes}
              isMultiple={false}
              disabled={transcript !== ''}
            />
          </Paper>
        </Box>

        {/* Step 2: Paste Description */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.875rem',
                fontWeight: 600,
                bgcolor: 'primary.light',
                color: 'primary.dark',
              }}
            >
              2
            </Avatar>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Or Paste Description
            </Typography>
          </Stack>
          <TextField
            multiline
            minRows={9}
            maxRows={9}
            fullWidth
            placeholder="Paste the Job Description here..."
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            disabled={fileUploaded}
            sx={{
              bgcolor: fileUploaded ? 'action.hover' : 'background.paper',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </Box>

      {/* Footer / Actions */}
      <Stack alignItems="center" spacing={1.5} sx={{ mt: 4 }}>
        <Button
          onClick={uploadJobDescription}
          variant="primary"
          label={isImporting ? 'Importing...' : 'Import Job Description'}
          disabled={!(fileUploaded || transcript !== '') || isImporting}
          icon={isImporting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : undefined}
          sx={{ width: 192 }}
        />
        {isImporting && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Processing your job description...
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ImportJobDescription;
